import { memo, useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { ChevronRight } from 'lucide-react'
import { Button } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable } from '@renderer/chat'
import { LinkPill } from './link-pill'
import type { BriefLink } from '../types'

interface BriefPreviewProps {
  brief: string
  relevantLinks: Array<BriefLink>
  className?: string
  /** Max number of links to render in the preview row. Defaults to 6. */
  maxLinks?: number
  /** Called when the preview itself is clicked (e.g. opens the brief drawer). */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void
  /** Render link pills as non-clickable chips so the whole card click passes through to onClick. */
  disableLinks?: boolean
  /**
   * Render the relevant-links row. On by default; Home's Upcoming card turns it off
   * because the design there is text-only (the meeting toasts keep their pills).
   */
  showLinks?: boolean
}

export const BriefPreview = memo(function BriefPreview({
  brief,
  relevantLinks,
  className,
  maxLinks = 6,
  onClick,
  disableLinks = false,
  showLinks = true,
}: BriefPreviewProps) {
  const visibleLinks = showLinks ? relevantLinks.slice(0, maxLinks) : []
  const trimmedBrief = brief.trim()

  const rowRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateOverflow = useCallback(() => {
    const el = rowRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = rowRef.current
    if (!el) return

    updateOverflow()

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const debouncedUpdate = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(updateOverflow, 50)
    }

    const resizeObserver = new ResizeObserver(debouncedUpdate)
    resizeObserver.observe(el)

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      resizeObserver.disconnect()
    }
  }, [updateOverflow, visibleLinks.length])

  const scroll = (direction: 'left' | 'right') => {
    const el = rowRef.current
    if (!el) return
    const amount = el.clientWidth * 0.6
    el.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    })
  }

  return (
    <div
      onClick={onClick}
      className={twMerge(
        // Text size lives on the container so the brief inherits it — that keeps
        // `className` able to resize the body copy (twMerge resolves the two
        // font-size utilities) without callers reaching at the inner <p>.
        'bg-background-subtle corner-smooth flex w-full min-w-0 max-w-full basis-full flex-col gap-2 overflow-hidden rounded-xl border p-3 text-sm',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      <p className="text-foreground line-clamp-2 break-words">{trimmedBrief}</p>
      {visibleLinks.length > 0 && (
        <div className="relative -ml-0.5 basis-full">
          <FadingScrollable
            ref={rowRef}
            direction="horizontal"
            fadeSize="36px"
            minFadeSize="0px"
            onScroll={updateOverflow}
            className="flex w-full min-w-0 items-center gap-1.5 overflow-x-hidden"
          >
            {visibleLinks.map((link) => (
              <LinkPill key={link.url} link={link} disabled={disableLinks} className="max-w-48" />
            ))}
          </FadingScrollable>

          {canScrollLeft && (
            <Button
              type="button"
              aria-label="Scroll left"
              shape="square"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                scroll('left')
              }}
              className="hover:bg-background-subtle absolute -left-1 top-1/2 -translate-y-1/2 shadow-none"
            >
              <ChevronRight className="text-foreground-secondary rotate-180" />
            </Button>
          )}

          {canScrollRight && (
            <Button
              type="button"
              aria-label="Scroll right"
              shape="square"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                scroll('right')
              }}
              className="hover:bg-background-subtle absolute -right-1 top-1/2 -translate-y-1/2 shadow-none"
            >
              <ChevronRight className="text-foreground-secondary" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
})
